<template>

<v-dialog v-model="show" id="send-token"
	persistent max-width="600px" :fullscreen="fullscreen">

	<v-card>

		<!--Modal Title -->
		<v-toolbar flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
			<v-toolbar-title>{{ $t('compSendToken.title') }}</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn icon @click="show = false">
				<v-icon>mdi-window-close</v-icon>
			</v-btn>
		</v-toolbar>

		<!-- Modal Contents -->
		<v-card-text class="pa-5">

			<div class="title text-center mb-3">
				{{ $t('compSendToken.intro', {tokenSymbol: inputTokenSymbol}) }}
			</div>

			<v-form ref="form" v-if="show && !txHash" v-model="valid">
				<v-text-field
					outlined color="gray" persistent-hint
					:label="$t('compSendToken.labelRecipientAdd')"
					id="send-token-recipient"
					v-model.trim="input.toAddress"
					:rules="rules.toAddress"
					clearable
					type="text"
					placeholder=""
				></v-text-field>

				<v-text-field
					outlined color="gray" persistent-hint
					:label="$t('compSendToken.labelAmount')"
					id="send-token-amount"
					v-model.number.trim="input.amount"
					:rules="rules.amount"
					type="number"
					placeholder=""
					:suffix="inputTokenSymbol"
					:hint="$t('common.noun.balance') + ': ' + formatAmountNumber(account.balanceByAddress[input.token.address])"
				></v-text-field>

				<div class="text-center my-3">
					<v-btn color="success" :disabled="!valid || loading" @click="sendToken()" v-show="!txHash" >
						{{ $t('compSendToken.btnSend') }} <ButtonLoading v-show="loading" class="ml-3" />
					</v-btn>
				</div>

			</v-form>

			<div class="text-center my-2 mx-3" v-if="txHash">
				<PendingTransaction :txhash="txHash" :txurl="networkConfig.txURL"></PendingTransaction>
			</div>

		</v-card-text>

		<v-card-actions v-if="fullscreen">
			<v-spacer></v-spacer>
			<v-btn color="primary" outlined text class="mr-3" @click="show = false">
				<v-icon>mdi-window-close</v-icon> {{ $t('common.verb.close')}}
			</v-btn>
		</v-card-actions>

	</v-card>
</v-dialog>

</template>

<script>

import { config } from '@/config'
import { ERC20TokenContract } from '@0x/contract-wrappers'
import { BigNumber } from '@0x/utils'
import { Web3Wrapper } from '@0x/web3-wrapper'
import PendingTransaction from "@/components/PendingTransaction"
import {mapActions, mapGetters, mapState} from 'vuex'
import { formatAmountNumber, countDecimals } from '@/utils/utils'
import ButtonLoading from "@/components/ButtonLoading"

export default {
	name: 'SendToken',
	components: {
		PendingTransaction,
		ButtonLoading
	},
	props: {
		fullscreen: { type: Boolean, default: false},
	},
	data () {
		return {
			input: {
				token: null,
				amount: null,
				toAddress: null,
			},
			valid: true,
			txHash: null,
			loading: false,
			show: false,
			rules: {
				amount: [
					// Field is required
					val => (val !== null && val !== undefined && val !== '') || this.$t('common.phrase.fieldIsRequired'),
					// Must be a number
					val => !isNaN(val) || this.$t('common.err.amount.mustNumber'),
					// Must greater than zero
					val => val > 0 || this.$t('common.err.amount.mustGreaterThanZero'),
					// Must less than the current balance
					val => val < this.account.balanceByAddress[this.input.token.address] ||
								this.$t('common.err.amount.insufficientBalance') + ": (" + this.$t('common.err.amount.max') + ': ' +
								formatAmountNumber(this.account.balanceByAddress[this.input.token.address]) + ')',
					// Must not have too many decimals
					val => (countDecimals(val) <= config.inputAmountMaxDecimals) ||
								this.$t('common.err.amount.tooManyDecimals') + ": (" + this.$t('common.err.amount.max') + ': ' +
								config.inputAmountMaxDecimals + ')',
				],
				toAddress: [
					// Field is required
					val => (val !== null && val !== undefined && val !== '') || this.$t('common.phrase.fieldIsRequired'),
					// Must be a valid ERC20 address
					val => Web3Wrapper.isAddress(val) || this.$t('compAddTokenTracking.searchErrInvalidAddress'),
					// Must not been added already
					val => val && val.toLowerCase() !== this.myAddress.toLowerCase() || this.$t('common.err.address.ownAdd'),
				]
			}
		}
	},
	computed: {
		...mapGetters({
			config: 'getConfig',
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			web3Wrapper: 'getWeb3Wrapper',
			providerEngine: 'getProviderEngine',
			contractWrappers: 'getContractWrappers',
			contractAddresses: 'getContractAddresses',
			networkConfig: 'getNetworkConfig',
		}),
		inputTokenSymbol: function () {
			if (this.input.token && this.input.token.symbol) {
				return this.input.token.symbol
			}
			else {
				return ''
			}
		}
	 },
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount']),
		formatAmountNumber: formatAmountNumber,
		countDecimals: countDecimals,
		/* ------------------------------------------------------ */
		async showForm (token) {
			if (!token) {
				throw new Error ("Missing token")
			}
			this.valid = true
			this.input = {
				token: token,
				amount: null,
				toAddress: null,
			}
			this.txHash = null
			this.loading = false
			this.show = true
		},
		async sendToken () {
			const token = this.input.token
			if (!token) {
				throw new Error ("Missing token info")
			}
			const toAddress = this.input.toAddress
			if (!toAddress) {
				throw new Error ("Missing to Address")
			}
			const amount = this.input.amount
			if (!amount) {
				throw new Error ("invalid token amount to send")
			}
			const amountToSend = Web3Wrapper.toBaseUnitAmount(new BigNumber(amount), token.decimals)
			this.loading = true
			try {
				const thisToken = new ERC20TokenContract(token.address, this.providerEngine)
				this.txHash = await thisToken.transfer(toAddress, amountToSend).sendTransactionAsync({
					from: this.myAddress
				})
				await this.web3Wrapper.awaitTransactionMinedAsync(this.txHash)
				await this.loadAccount(true)
				this.$parent.$emit("successNotification", this.$t('compSendToken.successMessage', {tokenSymbol: this.input.token.symbol, tokenAmount: this.input.amount }))
			} catch (err) {
				console.log(err)
			} finally {
				this.loading = false
				this.show = false
			}
		}
	},
	created () {
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
